import React, { useRef } from 'react';
import { FileText, Upload, X, Loader2, AlertCircle, GitCompare, Download, AlertTriangle } from 'lucide-react';

/**
 * Supporting documents for Contract Redlining.
 *
 * Two pickers live here because they mean different things to the review:
 *   - ReferenceDocumentsPicker — exhibits, addenda, supplements. Part of the contract, read
 *     with it during analysis. Redlines still go on the main contract only.
 *   - DiscrepancySection — scope documents and proposals, uploaded after the analysis and
 *     compared against the contract. Advisory: it never changes findings or redlines.
 */

export const MAX_SUPPORTING_DOCS = 5;

const isAcceptedDocument = (f) => [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
].includes(f.type) || /\.(pdf|docx?)$/i.test(f.name);

/** Add files to a list, keeping the cap and dropping duplicates by name and size. */
export const mergeSupportingFiles = (current, incoming, max = MAX_SUPPORTING_DOCS) => {
  const accepted = incoming.filter(isAcceptedDocument);
  const rejected = incoming.length - accepted.length;
  const seen = new Set(current.map((f) => `${f.name}|${f.size}`));
  const fresh = accepted.filter((f) => !seen.has(`${f.name}|${f.size}`));
  const room = Math.max(0, max - current.length);
  return { files: [...current, ...fresh.slice(0, room)], rejected, overflow: Math.max(0, fresh.length - room) };
};

export const SEVERITY_STYLES = {
  contradiction: { label: 'Contradiction', badge: 'bg-red-100 text-red-700 border-red-200', print: '#b91c1c' },
  tension: { label: 'Tension', badge: 'bg-amber-100 text-amber-800 border-amber-200', print: '#b45309' },
  consistent: { label: 'Consistent', badge: 'bg-green-100 text-green-700 border-green-200', print: '#15803d' },
};

const pagesLabel = (pages) => (pages?.length
  ? (pages.length === 1 ? `p. ${pages[0]}` : `pp. ${pages[0]}–${pages[pages.length - 1]}`)
  : '');

const FilePickerList = ({ files, onRemove, disabled }) => (
  files.length > 0 && (
    <ul className="mt-2 space-y-1">
      {files.map((f, i) => (
        <li key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-2 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate flex-1">{f.name}</span>
          <span className="text-gray-400 flex-shrink-0">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
          {!disabled && (
            <button type="button" onClick={() => onRemove(i)} className="p-0.5 text-gray-400 hover:text-red-600" title="Remove">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
);

const DropTarget = ({ inputRef, onFiles, disabled, label, hint }) => (
  <div
    onDrop={(e) => { e.preventDefault(); if (!disabled) onFiles(Array.from(e.dataTransfer.files || [])); }}
    onDragOver={(e) => e.preventDefault()}
    onClick={() => !disabled && inputRef.current?.click()}
    className={`border-2 border-dashed rounded-lg px-4 py-3 text-center transition-colors ${
      disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' : 'border-gray-300 hover:border-orange-400 cursor-pointer'
    }`}
  >
    <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
    <p className="text-xs font-medium text-gray-700">{label}</p>
    <p className="text-[11px] text-gray-500">{hint}</p>
    <input
      ref={inputRef}
      type="file"
      accept=".pdf,.docx,.doc"
      multiple
      className="hidden"
      onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
    />
  </div>
);

/** Exhibits, addenda and supplements, chosen with the contract before analysis. */
export const ReferenceDocumentsPicker = ({ files, onChange, disabled, blockedReason, onReject }) => {
  const inputRef = useRef(null);
  const add = (incoming) => {
    const { files: next, rejected, overflow } = mergeSupportingFiles(files, incoming);
    if (rejected || overflow) onReject?.(rejected, overflow);
    onChange(next);
  };
  return (
    <div className="mt-4 border border-gray-200 rounded-lg p-3 sm:p-4">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold text-gray-900">
          Supporting documents <span className="font-normal text-gray-500">(optional)</span>
        </h3>
        <span className="text-xs text-gray-500">{files.length}/{MAX_SUPPORTING_DOCS}</span>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        Exhibits, addenda, supplements or riders that belong to the contract. They are read with it as reference;
        redlines are placed on the main contract only.
      </p>
      {blockedReason ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{blockedReason}</p>
      ) : (
        <>
          {files.length < MAX_SUPPORTING_DOCS && (
            <DropTarget
              inputRef={inputRef}
              onFiles={add}
              disabled={disabled}
              label="Add exhibits, addenda or supplements"
              hint={`PDF or Word — up to ${MAX_SUPPORTING_DOCS} files`}
            />
          )}
          <FilePickerList files={files} disabled={disabled} onRemove={(i) => onChange(files.filter((_, j) => j !== i))} />
        </>
      )}
    </div>
  );
};

/** What the analysis read as reference, including any it could not. */
export const ReferenceDocumentsNote = ({ documents }) => {
  if (!documents?.length) return null;
  return (
    <div className="mt-2 pt-2 border-t border-gray-200">
      <p className="text-xs font-medium text-gray-700 mb-1">Supporting documents read as reference ({documents.length}):</p>
      {documents.map((d, i) => (
        <p key={i} className={`text-xs ${d.error ? 'text-red-600' : 'text-gray-600'}`}>
          • {d.fileName}
          {d.truncated && <span className="text-amber-700"> — only partly read (length)</span>}
          {d.error && <span> — could not be read: {d.error}</span>}
        </p>
      ))}
    </div>
  );
};

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/** A standalone HTML report of the comparison, for saving or printing to PDF. */
export const buildDiscrepancyReportHtml = (contractName, result) => {
  const position = (label, p) => `
    <div class="pos"><div class="lbl">${escapeHtml(label)}${p.pages?.length ? ` · ${escapeHtml(pagesLabel(p.pages))}` : ''}</div>
      <div>${escapeHtml(p.summary || '—')}</div>
      ${p.quote ? `<blockquote>“${escapeHtml(p.quote)}”${p.quoteVerified === false ? ' <em>(quote not found in the text — verify)</em>' : ''}</blockquote>` : ''}
    </div>`;
  const docs = (result.documents || []).map((doc) => `
    <section>
      <h2>${escapeHtml(doc.fileName)}${doc.documentType && doc.documentType !== 'other' ? ` <small>(${escapeHtml(doc.documentType)})</small>` : ''}</h2>
      ${doc.error ? `<p class="err">${escapeHtml(doc.error)}</p>` : ''}
      ${doc.summary ? `<p>${escapeHtml(doc.summary)}</p>` : ''}
      ${doc.coverageNote ? `<p class="note">${escapeHtml(doc.coverageNote)}</p>` : ''}
      ${!doc.error && !doc.findings?.length ? '<p class="note">No discrepancies reported for this document.</p>' : ''}
      ${(doc.findings || []).map((f) => `
        <div class="finding">
          <div><span class="sev" style="color:${SEVERITY_STYLES[f.severity]?.print || '#374151'}">${escapeHtml(SEVERITY_STYLES[f.severity]?.label || f.severity)}</span>
            <strong>${escapeHtml(f.topic)}</strong></div>
          <div class="grid">${position('Contract', f.contractPosition || {})}${position('Document', f.documentPosition || {})}</div>
          ${f.rationale ? `<p class="why">${escapeHtml(f.rationale)}</p>` : ''}
        </div>`).join('')}
    </section>`).join('');
  const c = result.counts || {};
  return `<!doctype html><html><head><meta charset="utf-8"><title>Scope &amp; Proposal Discrepancies — ${escapeHtml(contractName)}</title>
<style>
body{font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#111827;max-width:900px;margin:24px auto;padding:0 16px;font-size:13px;line-height:1.45}
h1{font-size:20px;margin:0 0 4px}h2{font-size:15px;margin:24px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
.meta,.note{color:#6b7280}.err{color:#b91c1c}.finding{border:1px solid #e5e7eb;border-radius:6px;padding:10px;margin:8px 0;page-break-inside:avoid}
.sev{font-weight:600;text-transform:uppercase;font-size:11px;margin-right:6px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px}
.lbl{font-size:11px;color:#6b7280;text-transform:uppercase}blockquote{margin:4px 0 0;padding-left:8px;border-left:3px solid #d1d5db;color:#374151;font-style:italic}
.why{margin:8px 0 0;color:#374151}.disclaimer{margin-top:24px;color:#6b7280;font-size:11px}
</style></head><body>
<h1>Scope &amp; Proposal Discrepancies</h1>
<div class="meta">Contract: ${escapeHtml(contractName)} · Generated ${escapeHtml(new Date(result.generatedAt || Date.now()).toLocaleString())}</div>
<p>${c.contradiction || 0} contradiction(s) · ${c.tension || 0} tension(s) · ${c.consistent || 0} confirmed</p>
${result.contractCoverageNote ? `<p class="note">${escapeHtml(result.contractCoverageNote)}</p>` : ''}
${docs}
<p class="disclaimer">${escapeHtml(result.disclaimer || '')}</p>
</body></html>`;
};

const downloadReport = (contractName, result) => {
  const html = buildDiscrepancyReportHtml(contractName, result);
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `discrepancies_${String(contractName || 'contract').replace(/\.(pdf|docx?)$/i, '')}.html`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const Position = ({ label, position }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-gray-500">
      {label}{position?.pages?.length ? ` · ${pagesLabel(position.pages)}` : ''}
    </p>
    <p className="text-xs text-gray-800">{position?.summary || '—'}</p>
    {position?.quote && (
      <blockquote className="mt-1 pl-2 border-l-2 border-gray-300 text-xs italic text-gray-600 break-words">
        “{position.quote}”
        {position.quoteVerified === false && (
          <span className="not-italic text-amber-700"> (quote not found in the text — verify)</span>
        )}
      </blockquote>
    )}
  </div>
);

/**
 * Compare scope documents and proposals against a finished analysis.
 *
 * Controlled by the page: `state` is { files, status, message, result, error } and lives on
 * the file entry, so each contract keeps its own comparison.
 */
export const DiscrepancySection = ({ contractName, state, canRun, unavailableReason, onFilesChange, onRun, onReject }) => {
  const inputRef = useRef(null);
  const { files = [], status = 'idle', message, result, error } = state || {};
  const running = status === 'running';
  const add = (incoming) => {
    const { files: next, rejected, overflow } = mergeSupportingFiles(files, incoming);
    if (rejected || overflow) onReject?.(rejected, overflow);
    onFilesChange(next);
  };
  const counts = result?.counts || {};

  return (
    <div className="mt-6 border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <GitCompare className="w-4 h-4 text-orange-600" />
          <h3 className="text-base font-semibold text-gray-900">Scope &amp; Proposal Discrepancies</h3>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">Advisory</span>
        </div>
        {result && (
          <button
            type="button"
            onClick={() => downloadReport(contractName, result)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Download className="w-3.5 h-3.5" /> Download report
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Upload the scope documents, proposals or bids this contract was based on to find where they disagree with it —
        price, scope, exclusions, schedule, rates. This does not change the review or the redlines above.
      </p>

      {unavailableReason ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{unavailableReason}</p>
      ) : (
        <>
          {files.length < MAX_SUPPORTING_DOCS && (
            <DropTarget
              inputRef={inputRef}
              onFiles={add}
              disabled={running}
              label="Add scope documents or proposals"
              hint={`PDF or Word — up to ${MAX_SUPPORTING_DOCS} files`}
            />
          )}
          <FilePickerList files={files} disabled={running} onRemove={(i) => onFilesChange(files.filter((_, j) => j !== i))} />
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={onRun}
              disabled={!canRun || running || files.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCompare className="w-3.5 h-3.5" />}
              {running ? 'Comparing…' : result ? 'Compare again' : 'Compare against contract'}
            </button>
            {running && message && <span className="text-xs text-blue-700">{message}</span>}
          </div>
        </>
      )}

      {status === 'error' && error && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {['contradiction', 'tension', 'consistent'].map((s) => (
              <span key={s} className={`px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_STYLES[s].badge}`}>
                {counts[s] || 0} {SEVERITY_STYLES[s].label.toLowerCase()}{s === 'consistent' ? '' : (counts[s] === 1 ? '' : 's')}
              </span>
            ))}
            {result.unverifiedQuotes > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                <AlertTriangle className="w-3 h-3" /> {result.unverifiedQuotes} quote(s) to verify
              </span>
            )}
          </div>
          {result.contractCoverageNote && <p className="text-xs text-gray-500 mb-2">{result.contractCoverageNote}</p>}

          <div className="space-y-4">
            {(result.documents || []).map((doc) => (
              <div key={`${doc.index}-${doc.fileName}`}>
                <p className="text-sm font-semibold text-gray-900">
                  {doc.fileName}
                  {doc.documentType && doc.documentType !== 'other' && (
                    <span className="ml-2 text-xs font-normal text-gray-500">{doc.documentType}</span>
                  )}
                </p>
                {doc.error && <p className="text-xs text-red-600">{doc.error}</p>}
                {doc.summary && <p className="text-xs text-gray-600">{doc.summary}</p>}
                {doc.coverageNote && <p className="text-xs text-gray-500 italic">{doc.coverageNote}</p>}
                {!doc.error && !doc.findings?.length && (
                  <p className="text-xs text-gray-500 mt-1">No discrepancies reported for this document.</p>
                )}
                <div className="mt-2 space-y-2">
                  {(doc.findings || []).map((f) => (
                    <div key={f.findingId} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${SEVERITY_STYLES[f.severity]?.badge || ''}`}>
                          {SEVERITY_STYLES[f.severity]?.label || f.severity}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{f.topic}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Position label="Contract" position={f.contractPosition} />
                        <Position label="Document" position={f.documentPosition} />
                      </div>
                      {f.rationale && <p className="mt-2 text-xs text-gray-700">{f.rationale}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-gray-500">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
};
